import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

import isEmpty from '../../utils/isEmpty';
import formatString from '../../utils/formatString';
import { Icons } from './Icons';

const colorFromString = (str) => {
	let h = 0;
	for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) >>> 0;
	return [(h & 0xff), ((h >> 8) & 0xff), ((h >> 16) & 0xff)];
};

const TagsChart = ({ appOrgConfig, tagsList, selectedResult, imageInfo }) => {
	const { t } = useTranslation();

	const [filterOptions, setFilterOptions] = useState([]);
	const [selectedOption, setSelectedOption] = useState('productos');

	useEffect(() => {
		if (!isEmpty(imageInfo) && appOrgConfig) {
			const cfg = appOrgConfig?.data?.display_results?.find((r) => r.r_type_id === selectedResult?.r_type_id);
			setFilterOptions(cfg?.tag_list_filters || []);
		} else {
			setFilterOptions([]);
		}
	}, [tagsList, imageInfo, appOrgConfig, selectedResult]);

	useEffect(() => {
		if (filterOptions.length > 0 && !filterOptions.includes(selectedOption) && selectedOption !== 'productos') {
			setSelectedOption(filterOptions[0]);
		}
	}, [filterOptions]);

	const rows = useMemo(() => {
		if (!tagsList || tagsList.length === 0) return [];
		const total = tagsList.reduce((sum, t) => sum + (t.quantity || 0), 0) || 1;

		let items;
		if (selectedOption === 'productos') {
			items = tagsList.map((tag) => ({
				key: `${tag.id}-${tag.className}`,
				name: tag.className,
				quantity: tag.quantity || 0,
				rgb: tag.arrayRGBColor || [120, 120, 120],
			}));
		} else {
			const grouped = {};
			tagsList.forEach((tag) => {
				const key = tag?.data?.[selectedOption] ?? '—';
				if (!grouped[key]) {
					grouped[key] = {
						key: String(key),
						name: String(key),
						quantity: 0,
						rgb: colorFromString(String(key)),
					};
				}
				grouped[key].quantity += tag.quantity || 0;
			});
			items = Object.values(grouped);
		}

		items.sort((a, b) => b.quantity - a.quantity);
		const top = items[0]?.quantity || 1;

		return items.map((r) => ({
			...r,
			pct: (r.quantity * 100) / total,
			fillPct: (r.quantity * 100) / top,
		}));
	}, [tagsList, selectedOption]);

	if (!tagsList || tagsList.length === 0) {
		return (
			<div className='dn-chart-empty'>
				<Icons.chart />
				<p>{t('tagChart.errors.noProducts', { defaultValue: 'No products to chart.' })}</p>
			</div>
		);
	}

	const allOptions = ['productos', ...filterOptions.filter((o) => o !== 'productos')];

	return (
		<div className='dn-chart'>
			<div className='dn-chart-header'>
				<div className='dn-chart-title'>
					<Icons.chart /> {t('display.chart.title', { defaultValue: 'Products distribution' })}
				</div>
				{allOptions.length > 1 && (
					<div className='dn-recon-toggle'>
						{allOptions.map((opt) => (
							<label key={opt} className={`dn-pill${selectedOption === opt ? ' active' : ''}`}>
								<input
									type='radio'
									name='chartGroup'
									value={opt}
									checked={selectedOption === opt}
									onChange={(e) => setSelectedOption(e.target.value)}
								/>
								{formatString(opt)}
							</label>
						))}
					</div>
				)}
			</div>
			<div className='dn-chart-bars'>
				{rows.map((row) => {
					const [r, g, b] = row.rgb;
					return (
						<div key={row.key} className='dn-chart-bar'>
							<span className='dn-chart-bar-color' style={{ background: `rgb(${r}, ${g}, ${b})` }} aria-hidden='true' />
							<span className='dn-chart-bar-name' title={row.name}>{row.name}</span>
							<span className='dn-chart-bar-qty'>{row.quantity}</span>
							<span className='dn-chart-bar-track'>
								<span
									className='dn-chart-bar-fill'
									style={{
										width: `${row.fillPct}%`,
										background: `rgba(${r}, ${g}, ${b}, 0.85)`,
									}}
								/>
							</span>
							<span className='dn-chart-bar-pct'>{row.pct.toFixed(1)}%</span>
						</div>
					);
				})}
			</div>
		</div>
	);
};

TagsChart.propTypes = {
	appOrgConfig: PropTypes.object,
	tagsList: PropTypes.array,
	selectedResult: PropTypes.object,
	imageInfo: PropTypes.object,
};

export default TagsChart;
